import React, { useEffect } from 'react';
import { X, Check, Plus, Settings } from 'lucide-react';
import { useFamilyGroupStore } from '../../store/familyGroupStore';

interface FamilySelectorSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFamily: () => void;
  onManageFamily: (familyId: string) => void;
}

const FamilySelectorSheet: React.FC<FamilySelectorSheetProps> = ({
  isOpen,
  onClose,
  onCreateFamily,
  onManageFamily
}) => {
  const { familyGroups, activeFamilyId, setActiveFamilyId } = useFamilyGroupStore();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSelectFamily = (familyId: string) => {
    setActiveFamilyId(familyId);
    onClose();
  };

  const handleManageFamily = (e: React.MouseEvent, familyId: string) => {
    e.stopPropagation();
    onClose();
    onManageFamily(familyId);
  };

  const handleCreateFamily = () => {
    onClose();
    onCreateFamily();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal">
      {/* Mobile: dimmed backdrop / Desktop: transparent click-away */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm lg:bg-black/20 lg:backdrop-blur-none"
        onClick={onClose}
      />

      {/* Mobile: bottom sheet / Desktop: dropdown below header */}
      <div
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl animate-slide-up lg:bottom-auto lg:left-1/2 lg:right-auto lg:top-16 lg:-translate-x-1/2 lg:rounded-xl lg:w-80 lg:shadow-xl lg:border lg:border-stone-200 lg:animate-fade-in"
        style={{
          maxHeight: '70dvh',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        <div className="lg:hidden flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-stone-300 rounded-full" />
        </div>

        <div className="px-6 pb-3 pt-2 lg:pt-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-stone-900">
            Mis Familias
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors flex items-center justify-center"
          >
            <X className="h-5 w-5 text-stone-600" />
          </button>
        </div>

        <div className="px-4 pb-6 overflow-y-auto" style={{ maxHeight: 'calc(70dvh - 80px)' }}>
          <div className="space-y-2">
            {familyGroups.map((family) => {
              const isActive = family.id === activeFamilyId;
              // The manage (gear) control sits OUTSIDE the row button via absolute
              // positioning — nesting button-in-button is invalid HTML and React
              // logs validateDOMNesting errors (caught by the PRO-344 e2e spec).
              return (
                <div key={family.id} className="relative">
                  <button
                    onClick={() => handleSelectFamily(family.id)}
                    className={`w-full flex items-center justify-between p-3 pr-12 rounded-xl transition-all ${
                      isActive
                        ? 'bg-rose-50 border-2 border-rose-300'
                        : 'bg-white border-2 border-stone-200 hover:border-rose-300 hover:bg-rose-50/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                        isActive ? 'bg-rose-200 text-rose-700' : 'bg-rose-100 text-rose-500'
                      }`}>
                        {family.name.charAt(0)}
                      </div>
                      <div className="text-left">
                        <p className={`font-medium ${isActive ? 'text-rose-900' : 'text-stone-800'}`}>
                          {family.name}
                        </p>
                      </div>
                    </div>

                    {isActive && (
                      <Check className="h-5 w-5 text-rose-600" />
                    )}
                  </button>
                  <button
                    onClick={(e) => handleManageFamily(e, family.id)}
                    aria-label={`Gestionar ${family.name}`}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-stone-200 rounded-full transition-colors !min-h-0 !min-w-0"
                  >
                    <Settings className="h-4 w-4 text-stone-500" />
                  </button>
                </div>
              );
            })}

            <button
              onClick={handleCreateFamily}
              className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-stone-300 hover:border-rose-400 hover:bg-rose-50/50 transition-all"
            >
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                <Plus className="h-5 w-5 text-rose-600" />
              </div>
              <span className="font-medium text-stone-700">
                Crear nueva familia
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilySelectorSheet;
